import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail, setAuthToken, setRefreshToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, object=auth
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  // Listen for global "auth:logout" event (fired by axios interceptor when
  // refresh fails). Clear user state so protected routes redirect to /login.
  useEffect(() => {
    const onForcedLogout = () => {
      setAuthToken(null);
      setRefreshToken(null);
      setUser(false);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("auth:logout", onForcedLogout);
      return () => window.removeEventListener("auth:logout", onForcedLogout);
    }
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAuthToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) setAuthToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setAuthToken(null);
    setRefreshToken(null);
    setUser(false);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
